import { Component, ElementRef, inject, Input, OnInit, signal, ViewChild } from "@angular/core";
import { DOCUMENT, JsonPipe, Location, NgTemplateOutlet } from "@angular/common";
import { expandNavItemAnimation } from "../../animations/expand-nav-item.animation";
import { SideNavStatusEnum } from "../../interfaces/side-nav-status.enum";
import { SideNavService } from "../../services/side-nav.service";
import { ToggleSideNavDirective } from "../../directives/toggle-side-nav.directive";
import { NavigationCancel, NavigationEnd, Router, RouterLink, RouterLinkActive } from "@angular/router";
import { SelectedItemDirective } from "../../directives/selected-item.directive";
import { Tooltip } from "primeng/tooltip";
import { TranslatePipe } from "@ngx-translate/core";
import { SidenavModel } from "../../models/sidenav.model";
import { SvgIconComponent } from "@kernel/tools/svg-icon/components/svg-icon/svg-icon.component";
import { environment } from "@environments/environment";
import { filter } from "rxjs";
import { untilDestroyed } from "@shared-core/helpers/until-destroy.helper";

@Component({
  selector: "sxw-side-nav",
  templateUrl: "./side-nav.component.html",
  styleUrls: ["./side-nav.component.scss"],
  animations: [expandNavItemAnimation],
  imports: [
    ToggleSideNavDirective,
    RouterLink,
    SelectedItemDirective,
    Tooltip,
    TranslatePipe,
    RouterLinkActive,
    SvgIconComponent,
    NgTemplateOutlet,
    JsonPipe,
  ],
})
export class SideNavComponent implements OnInit {
  private document = inject(DOCUMENT);
  readonly sideNavService = inject(SideNavService);
  private location = inject(Location);

  router = inject(Router);

  @Input() width = signal("303px");
  @Input() agencyName = signal("Storex");
  @Input() agencyExtraName = signal("Web");
  @Input() agencyNameStyle = signal("agency-name");
  @Input() agencyExtraNameStyle = signal("agency-extra-name");

  @ViewChild("arrowEl") arrowEl?: ElementRef<HTMLDivElement>;

  sideNavStatusEnum = SideNavStatusEnum;
  sideNavStatus = signal(SideNavStatusEnum.collapse);
  currentUrl = this.location.path() || "/";
  // this flag to determine if the user click a link to navigate or not
  clickMode = false;

  noUser = signal(environment.skip_auth);

  takeUntilDestroy = untilDestroyed();

  ngOnInit() {
    this.sideNavService.searchMenuActivatedRoute(this.currentUrl);
    this.updateSideNavStatus();

    this.observeRouteNavigationEnd();
    this.sideNavService
      .observeUserAuthorization()
      .pipe(this.takeUntilDestroy())
      .subscribe(() => this.updateSideNavStatus());
  }

  /**
   * Observe route navigation end if it's fired when the user click the link will do nothing, but if the user use browser history
   * to go back and forward it will fire and activate the link
   * @private
   */
  private observeRouteNavigationEnd() {
    this.router.events
      .pipe(
        this.takeUntilDestroy(),
        filter(e => e instanceof NavigationEnd || e instanceof NavigationCancel)
      )
      .subscribe(_ => {
        if (this.clickMode) {
          this.clickMode = false;
          return;
        }
        this.sideNavService.updateLinksState();
        this.updateSideNavStatus();
      });
  }

  setSelectedItem(status: SideNavStatusEnum | null): void {
    if (!status) this.sideNavStatus.set(SideNavStatusEnum.collapse);
  }

  /**
   * Toggle expanded menu items (open / close them)
   * @param item
   */
  toggleExpandMenuItem(item: SidenavModel): void {
    this.sideNavService.toggleExpandItem(item);
  }

  /**
   * it will handle checkMode, and clickable of main links and sublinks
   * @param item
   */
  clickItem(item: SidenavModel) {
    if (item.perform) {
      return item.perform(item);
    }
    this.clickMode = true;
    if (item.parent?.id === "root") {
      this.sideNavService.setSelectedMainLink(item.id);
    } else {
      this.toggleExpandMenuItem(item);
    }
    if (!item.routerLink) this.clickMode = false;
  }

  private updateSideNavStatus() {
    this.sideNavStatus.set(
      this.sideNavService.selectedMainLink()?.items?.length
        ? this.document.body.offsetWidth <= 500
          ? SideNavStatusEnum.mouseenter
          : SideNavStatusEnum.expanded
        : SideNavStatusEnum.collapse
    );
  }

}
